// نوار هشدار لایسنس On-prem (US-PLT-042، AC-PLT-042.1): از ۳۰ روز مانده هشدار، در مهلت ۱۴ روزه (دسترسی کامل) نوار
// برجسته و پس از آن ReadOnly. فقط برای کسی که وضعیت لایسنس را می‌بیند (plt.subscription.view) درخواست می‌رود و در
// استقرار SaaS وضعیت «لایسنسی نصب نیست» است و نواری نمایش داده نمی‌شود. شکست دریافت پوسته را مختل نمی‌کند.
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import { unwrap } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { faDigits, JalaliDate } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router';
import { pltKeys, toCount } from '../pages/settings/common';

/** مسیر صفحه نصب (SCR-STAFF-910) — مقصد دکمه «تمدید». */
export const INSTALLATION_PATH = '/settings/installation';

/** Permissionهای خواندن وضعیت لایسنس (یکی کافی است — همان Endpoint؛ C-AGENT-1627). */
export const LICENSE_STATUS_PERMISSIONS = ['plt.subscription.view', 'onprem.license.install'] as const;

export function LicenseBanner() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const status = useQuery({
    queryKey: pltKeys.license(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/license')),
    // بدون Permission خواندن وضعیت، Endpoint ۴۰۳ می‌دهد؛ پس درخواستی فرستاده نمی‌شود.
    enabled: Boolean(session?.tenantId) && LICENSE_STATUS_PERMISSIONS.some((code) => access.can(code)),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const data = status.data;

  if (!data || data.state === 'Active' || data.state === 'Missing') {
    return null;
  }

  const days = toCount(data.daysLeft);
  const severity = data.state === 'Expiring' ? 'warning' : 'error';

  return (
    <Alert
      severity={severity}
      sx={{ mb: 1 }}
      data-testid="license-banner"
      data-license-state={data.state}
      action={
        <Button component={RouterLink} to={INSTALLATION_PATH} color="inherit" size="small">
          {t('plt:installation.banner.renew')}
        </Button>
      }
    >
      <AlertTitle>{t(`plt:installation.banner.${data.state.toLowerCase()}`)}</AlertTitle>
      {data.state === 'Expiring' && t('plt:installation.days_left', { days: faDigits(days) })}
      {data.state === 'Grace' && data.graceEndsAt && (
        <>
          {t('plt:installation.grace.body')} <JalaliDate value={data.graceEndsAt} />
        </>
      )}
      {data.state === 'Expired' && t('plt:installation.expired_read_only')}
    </Alert>
  );
}
